// Token artwork through the safe image proxy only (api-surface section 3):
// GET /commercial-api/token-image?url=<https url>. Session required upstream,
// host allowlist of eight CDNs, 2 MB cap. Anything that is not https, or that
// the proxy rejects, renders the neutral "Artwork unavailable" glyph. No
// initials and no coloured placeholder are ever drawn.

import { useEffect, useState } from 'react';
import { ImageSquare } from '@phosphor-icons/react';
import { API_PATHS } from '../api/config';
import './TokenArtwork.css';

export const ARTWORK_UNAVAILABLE_LABEL = 'Artwork unavailable';

export function proxiedArtworkUrl(imageUrl: string | null | undefined): string | null {
  if (!imageUrl) return null;
  try {
    const url = new URL(imageUrl);
    if (url.protocol !== 'https:') return null;
    return `${API_PATHS.tokenImage}?${new URLSearchParams({ url: url.href })}`;
  } catch {
    return null;
  }
}

export interface TokenArtworkProps {
  readonly imageUrl: string | null | undefined;
  // Ticker or name for the alt text; the glyph carries its own label.
  readonly symbol: string | null | undefined;
  // Pixel size. Omit to inherit the density token (--art) from the row.
  readonly size?: number | undefined;
  readonly className?: string | undefined;
  // Told whenever a real image is or is not showing (source rejected, or the
  // proxied fetch failed), so a caller like ArtworkZoom can decide there is
  // nothing to enlarge without duplicating the source/failure check.
  readonly onAvailabilityChange?: (available: boolean) => void;
}

export function TokenArtwork({ imageUrl, symbol, size, className, onAvailabilityChange }: TokenArtworkProps) {
  const source = proxiedArtworkUrl(imageUrl);
  // Remembering which source failed (rather than a boolean) resets the state
  // when the source changes without an effect.
  const [failedSource, setFailedSource] = useState<string | null>(null);
  const failed = source !== null && failedSource === source;
  const available = source !== null && !failed;
  useEffect(() => { onAvailabilityChange?.(available); }, [available, onAvailabilityChange]);
  const style = size === undefined ? undefined : { width: size, height: size };
  const classes = ['token-artwork', className].filter(Boolean).join(' ');

  if (!source || failed) {
    return (
      <span
        className={`${classes} token-artwork-unavailable`}
        role="img"
        aria-label={ARTWORK_UNAVAILABLE_LABEL}
        title={ARTWORK_UNAVAILABLE_LABEL}
        style={style}
      >
        <ImageSquare aria-hidden="true" weight="regular" />
      </span>
    );
  }

  const alt = symbol ? `${symbol} token artwork` : 'Token artwork';
  return (
    <img
      className={classes}
      src={source}
      alt={alt}
      decoding="async"
      loading="lazy"
      style={style}
      onError={() => setFailedSource(source)}
    />
  );
}
